import { OnetrustConsentProvider } from './onetrust-consent.provider'
import { createOptanonWrapper } from './optanon-wrapper/optanon-wrapper'
import { setOneTrustVariantCookie } from './model/set-one-trust-variant-cookie'
import { ConsentProviderConfig, LoadConsentProviderPort } from '../ports'
import {
  EnvironmentConfig,
  LoggingPort,
  RetryStrategy,
} from '@otto-ec/eprivacy-shared'
import { getOnetrustSdkSrc } from './model/get-onetrust-sdk-src'
import { loadOnetrustSdk } from './load-one-trust-sdk/load-onetrust-sdk'
import { ConsentTtlService } from './optanon-consent-ttl/consent-ttl.service'
import { LazyOnetrustConsentProvider } from './lazy-onetrust-consent.provider'
import { OneTrust } from './model/onetrust'

export const loadOneTrustConsentProviderFactory = (
  logger: LoggingPort,
  envConfig: EnvironmentConfig,
  isOneTrustInternalHostingEnabled: boolean,
  retryWrapper: RetryStrategy,
  lazyLoading: boolean
) => {
  const loadOnetrustConsentProvider: LoadConsentProviderPort = async (
    config: ConsentProviderConfig,
    onetrustVariantId?: string
  ) => {
    if (lazyLoading) {
      return loadLazyOneTrustProvider(
        onetrustVariantId,
        envConfig,
        config,
        isOneTrustInternalHostingEnabled,
        retryWrapper,
        logger
      )
    }
    return await loadOneTrustProvider(
      onetrustVariantId,
      envConfig,
      config,
      isOneTrustInternalHostingEnabled,
      retryWrapper,
      logger
    )
  }

  return loadOnetrustConsentProvider
}

function loadLazyOneTrustProvider(
  onetrustVariantId: string | undefined,
  envConfig: EnvironmentConfig,
  config: ConsentProviderConfig,
  isOneTrustInternalHostingEnabled: boolean,
  retryStrategy: RetryStrategy,
  logger: LoggingPort
) {
  /*                                                               */
  /*                                                        */
  const oneTrustPromiseFn = async () =>
    configureAndLoadOneTrustSdk(
      onetrustVariantId,
      envConfig,
      config,
      isOneTrustInternalHostingEnabled,
      retryStrategy,
      logger
    )

  return new LazyOnetrustConsentProvider(
    oneTrustPromiseFn,
    new ConsentTtlService(logger),
    logger
  )
}

async function configureAndLoadOneTrustSdk(
  onetrustVariantId: string | undefined,
  envConfig: EnvironmentConfig,
  config: ConsentProviderConfig,
  isOneTrustInternalHostingEnabled: boolean,
  retryWrapper: RetryStrategy,
  logger: LoggingPort
) {
  const oneTrustPromise = new Promise<OneTrust>((resolve, reject) =>
    createOptanonWrapper(resolve, reject)
  )

  if (onetrustVariantId) {
    setOneTrustVariantCookie(onetrustVariantId, envConfig.stage)
  }

  const oneTrustSrc = getOnetrustSdkSrc(
    config.onetrust.onetrustSiteConfig,
    envConfig.stage,
    isOneTrustInternalHostingEnabled
  )

  const checkScriptIntegrity = !isOneTrustInternalHostingEnabled

  let failedAttempts = 0

  await retryWrapper.run<void>(
    () => loadOnetrustSdk(config, oneTrustSrc, checkScriptIntegrity),
    () => failedAttempts++
  )
  if (failedAttempts > 0) {
    logger.info(
      `Successfully loaded OneTust-SDK after ${failedAttempts} attempts.`
    )
  }
  return oneTrustPromise
}

async function loadOneTrustProvider(
  onetrustVariantId: string | undefined,
  envConfig: EnvironmentConfig,
  config: ConsentProviderConfig,
  isOneTrustInternalHostingEnabled: boolean,
  retryWrapper: RetryStrategy,
  logger: LoggingPort
) {
  const oneTrust = await configureAndLoadOneTrustSdk(
    onetrustVariantId,
    envConfig,
    config,
    isOneTrustInternalHostingEnabled,
    retryWrapper,
    logger
  )
  return new OnetrustConsentProvider(oneTrust, new ConsentTtlService(logger))
}
